import Vue from 'vue';
import { getSegmentRecommendations } from './smart-segments.utils';
import { LOADING_STATES } from '@/constants';
import { formatMetadataForRules } from '@/segments/components/segment-editor-vue/segment-editor.utils';
import get from 'lodash/get';

const { LOADING, RESOLVED, EMPTY, REJECTED } = LOADING_STATES;
const MIN_BASE_VISITORS_COUNT = 100;

const smartSegments = {
    state: {
        status: null,
        map: {},
    },
    namespaced: true,
    mutations: {
        addOrUpdateSegmentsInMap (state, { targetId, segmentId, recommendedSegments = [] }) {
            Vue.set(state.map, `${targetId}|${segmentId}`, recommendedSegments);
        },
        setStatus (state, newStatus) {
            state.status = newStatus;
        },
    },
    actions: {
        async getSegmentRecommendations (
            { commit, state },
            { appId, itemId, itemType, segmentId = 'custom', audience = [], baseVisitorsCount },
        ) {
            if (baseVisitorsCount < MIN_BASE_VISITORS_COUNT) {
                commit('setStatus', EMPTY);

                return;
            }

            const smartSegmentKey = `${itemId}|${segmentId}`;

            if (state.map[smartSegmentKey]) {
                commit('setStatus', RESOLVED);

                return;
            }

            try {
                commit('setStatus', LOADING);
                const { smartSegments } = await getSegmentRecommendations(
                    appId,
                    itemId,
                    itemType,
                    audience,
                    baseVisitorsCount,
                );

                commit('setStatus', smartSegments.length > 0 ? RESOLVED : EMPTY);

                if (smartSegments.length > 0) {
                    commit('addOrUpdateSegmentsInMap', {
                        targetId: itemId,
                        segmentId,
                        recommendedSegments: smartSegments,
                    });
                }
            } catch (error) {
                commit('setStatus', REJECTED);
                throw new Error(`Failed to fetch segment recommendations: ${error}`);
            }
        },
        setEligibleVisitorsForRecommendedSegments ({ getters }, eligibleVisitorsForRecommendations) {
            getters.currentRecommendedRules.forEach((recommendation, index) => {
                const fieldName = `eligibleVisitorCount${index + 1}`;
                const eligibleVisitorsForRecommendation = eligibleVisitorsForRecommendations.find(
                    (rule) => rule[fieldName],
                );
                recommendation.eligibleVisitors = eligibleVisitorsForRecommendation[fieldName];
            });
        },
    },
    getters: {
        currentRecommendedRules: (state, _getters, _rootState, _rootGetters) => {
            // eslint-disable-next-line no-warning-comments
            const { useJourneysStore } = require('@/stores/journeys.store'); // TODO APP-157977: journeys migrated to Pinia
            const journeysStore = useJourneysStore();
            const journey = journeysStore.getActiveJourney;
            const journeySegmentId = get(journey, 'audienceUiHint.filters[0].segmentId', 'custom');
            const segmentId = journeySegmentId === 'noOne' ? 'everyone' : journeySegmentId;
            const { itemId } = journeysStore.getGoal;

            return state.map[`${itemId}|${segmentId}`] || [];
        },
        segmentsFromRecommendedRules (state, { currentRecommendedRules }, rootState, rootGetters) {
            const metadataList = rootGetters['metadata/metadataList'];

            // eslint-disable-next-line no-warning-comments
            const { useJourneysStore } = require('@/stores/journeys.store'); // TODO APP-157977: journeys migrated to Pinia
            const journey = useJourneysStore().getActiveJourney;
            const baseSegmentId = get(journey, 'audienceUiHint.filters[0].segmentId', 'custom');

            const formatedMetadataForRules = formatMetadataForRules(metadataList);

            return currentRecommendedRules.map(({ key, op, value }) => {
                const formatedMetadataForRule = formatedMetadataForRules.find((rule) => rule.field.includes(key));

                const recommendedRule = {
                    ...formatedMetadataForRule,
                    operator: op,
                    value,
                };

                let newSegmentfilters;
                if (baseSegmentId !== 'custom') {
                    const baseSegment = rootGetters['segments/segmentById'](baseSegmentId);
                    newSegmentfilters = [...get(baseSegment, 'definition.filters', []), recommendedRule];
                } else {
                    newSegmentfilters = [...journey.audienceUiHint.filters, recommendedRule];
                }

                const definition = { filters: newSegmentfilters };
                const name = `<${value}> segment recommendation`;

                return { definition, name, shared: true };
            });
        },
    },
};

export default smartSegments;
